import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import CertificatesEditingWidget from "../../widgets/profile-edit/CertificatesEditing";

import ExperienceEditingWidget from "../../widgets/profile-edit/ExperienceEditing";
import GoalsAndPreferencesWidget from "../../widgets/profile-edit/GoalsAndPreferences";
import SkillsEditingWidget from "../../widgets/profile-edit/SkillsEditing";

import { MainLayout } from "../../layouts/MainLayout";
import {
  Box,
  Paper,
  ScrollArea,
  Group,
  Text,
  Flex,
  Title,
  Button,
  Modal,
  Tooltip,
  useMantineTheme,
  rem,
  ActionIcon,
} from "@mantine/core";

import { useMediaQuery } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import useProfileStore from "../../store/useProfileStore";
import useAuthStore from "../../store/useAuthStore";
import { EditContactInfo } from "../../widgets/profile-edit/EditContactInfo";
import EducationEditingWidget from "../../widgets/profile-edit/EducationEditing";
import HobbiesEditingWidget from "../../widgets/profile-edit/HobbiesEditing";
import useReportStore from "../../store/useReportStore";
import { ClearIconWhite } from "../../shared/svg/ClearIcon";
import { processUserPrice } from "../../api/reportApi";

const ProfileEdit = () => {
  const { page } = useParams();
  const mobile = useMediaQuery("(max-width: 820px)");
  const { t } = useTranslation();
  const navItems = [
    { labelKey: t("profileEdit.contacts"), value: "contacts" },
    { labelKey: t("profileEdit.goals"), value: "goals" },
    { labelKey: t("profileEdit.education"), value: "education" },
    { labelKey: t("profileEdit.experience"), value: "experience" },
    { labelKey: t("profileEdit.certificates"), value: "certificates" },
    { labelKey: t("profileEdit.skills"), value: "skills" },
    { labelKey: t("hobby"), value: "hobby" },
  ];

  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const theme = useMantineTheme();
  const { access_token } = useAuthStore();
  const {
    fetchProfile,
    changes,
    setChanges,
    setUserMarketChanges,
    userMarketChanges,
  } = useProfileStore();
  const { makeReport } = useReportStore();
  const [errorMessage, setErrorMessage] = useState("");

  const [tooltipOpened, setTooltipOpened] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => setTooltipOpened(true), 500);

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }, 300);
  }, []);

  useEffect(() => {
    if (access_token) fetchProfile();
  }, [access_token]);

  const renderPage = () => {
    switch (page) {
      case "contacts":
        return <EditContactInfo setChanges={setChanges} />;
      case "certificates":
        return <CertificatesEditingWidget setChanges={setChanges} />;
      case "education":
        return <EducationEditingWidget setChanges={setChanges} />;
      case "experience":
        return <ExperienceEditingWidget setChanges={setChanges} />;
      case "goals":
        return <GoalsAndPreferencesWidget setChanges={setChanges} />;
      case "skills":
        return <SkillsEditingWidget setChanges={setChanges} />;
      case "hobby":
        return <HobbiesEditingWidget setChanges={setChanges} />;
      default:
        return null;
    }
  };

  const handleMakeReport = async () => {
    setErrorMessage("");

    if (changes) {
      try {
        const res = await makeReport();
        if (!res) {
          setErrorMessage(t("no_cv"));
          return;
        }
        setChanges(false);

        console.log(userMarketChanges);
        if (userMarketChanges === true) {
          processUserPrice();
          setUserMarketChanges(false);
        }

        navigate("/profile");
      } catch (error: any) {
        console.log(error);
        if (error?.status === 400) setErrorMessage(t("not_all_answers"));
      }
    }
  };

  return (
    <MainLayout>
      <Box m={mobile ? 16 : 40} mt={mobile ? 8 : 20} mih="100vh">
        <Flex
          align={mobile ? "flex-end" : "center"}
          justify="space-between"
          direction={mobile ? "column" : "row"}
          mb={15}
          gap={20}
        >
          <ScrollArea
            type="always"
            offsetScrollbars
            scrollbarSize={0}
            mt={mobile ? 10 : undefined}
            style={{
              flex: 1,
              minWidth: 0,
              maxWidth: "100%",
              overflowX: "auto",
            }}
          >
            <Group wrap="nowrap" gap={mobile ? 16 : 32}>
              {navItems.map((item) => (
                <Link
                  key={item.value}
                  to={`/profile/edit/${item.value}`}
                  style={{
                    textDecoration: "none",
                    paddingBottom: 8,
                    borderBottom:
                      item.value === page
                        ? "2px solid #6f40e2"
                        : "2px solid transparent",
                    color: item.value === page ? "#6f40e2" : "#495057",
                    fontWeight: item.value === page ? 600 : 500,
                    whiteSpace: "nowrap",
                    transition: "border-color 0.2s ease, color 0.2s ease",
                  }}
                >
                  <Text fw={600} size={mobile ? "sm" : "md"}>
                    {item.labelKey}
                  </Text>
                </Link>
              ))}
            </Group>
          </ScrollArea>

          <Tooltip
            label={
              <Group justify="space-between" align="flex-start" gap={0}>
                <Flex justify="space-between" w="100%" pos="relative">
                  <div />
                  <ActionIcon
                    onClick={() => setTooltipOpened(false)}
                    aria-label="Close"
                    variant="subtle"
                    color="white"
                    style={{
                      top: -22,
                      right: -18,
                      position: "absolute",
                      pointerEvents: "all",
                    }}
                  >
                    <ClearIconWhite />
                  </ActionIcon>
                </Flex>
                <Text
                  style={{
                    fontSize: mobile ? rem(14) : rem(16),
                    maxWidth: rem(460),
                    lineHeight: 1.4,
                  }}
                >
                  <b>{t("editing_end_start")}</b>
                  {t("editing_end_notification")}
                  <b>{t("editing_end_final")}</b>
                </Text>
                <Flex justify="flex-end" mt="sm" w="100%" align="flex-end">
                  <Button
                    onClick={() => setTooltipOpened(false)}
                    size="xs"
                    style={{
                      cursor: "pointer",
                      pointerEvents: "all",
                    }}
                  >
                    {t("got_it")}
                  </Button>
                </Flex>
              </Group>
            }
            withArrow
            opened={tooltipOpened}
            position="bottom"
            offset={8}
            arrowSize={12}
            transitionProps={{ duration: 150, transition: "fade" }}
            styles={{
              tooltip: {
                backgroundColor: theme.colors.violet[6],
                color: theme.white,
                fontSize: rem(16),
                padding: `${rem(30)} ${rem(26)} ${rem(20)} ${rem(26)}`,
                borderRadius: rem(16),
                boxShadow: theme.shadows.lg,
                maxWidth: mobile ? rem(325) : rem(500),
                lineHeight: 1.4,
                wordWrap: "break-word" as "break-word",
                whiteSpace: "normal",
              },
              arrow: {
                backgroundColor: theme.colors.violet[6],
              },
            }}
          >
            <Button
              style={{
                flexShrink: 0,
                maxWidth: 222,
                padding: "4px 12px",
                fontSize: "14px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              variant="outline"
              my={mobile ? 10 : undefined}
              onClick={() => setIsOpen(true)}
              disabled={!changes}
            >
              {t("end_editing")}
            </Button>
          </Tooltip>
        </Flex>

        <Paper
          withBorder
          radius="24px"
          p={mobile ? 24 : 32}
          h="100%"
          pos="relative"
          mt="md"
        >
          <div>{renderPage()}</div>
        </Paper>

        <Modal
          opened={isOpen}
          onClose={() => setIsOpen(false)}
          centered
          size="lg"
          zIndex={500}
        >
          <Flex align="center" direction="column">
            <Title style={{ textAlign: "center" }} size={20}>
              {t("after_changes")}
            </Title>
            {errorMessage && (
              <Text color="#B00020" size="sm" mt={10}>
                {errorMessage}
              </Text>
            )}
            <Flex align="center" gap={20} p={20} justify="space-around">
              <Button
                maw={240}
                variant="outline"
                onClick={() => {
                  setIsOpen(false);
                }}
                disabled={!changes}
              >
                {t("back_to_edit")}
              </Button>
              <Button
                maw={240}
                variant="filled"
                onClick={handleMakeReport}
                disabled={!changes}
              >
                {t("generate_report")}
              </Button>
            </Flex>
          </Flex>
        </Modal>
      </Box>
    </MainLayout>
  );
};

export default ProfileEdit;
